import ColorPicker from '@/components/commons/color-picker';
import { ElementProps } from '@/components/sections/builder/types';
import { ProductListElementDataType } from '@/components/sections/builder/elements/body/product-grid/type';
import BackgroundSetting from '@/components/sections/builder/settings/background-setting';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import InputTab from '@/components/sections/builder/settings/input-tab';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';
import { Checkbox } from '@/components/ui/checkbox';

import { useBuilderStore } from '@/stores/builder';
import { ColorResult } from 'react-color';
import { shallow } from 'zustand/shallow';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';
import { DEFAULT_THEME_COLOR_SCHEME } from '@/components/sections/builder/theme/color';

export default function ElementSettingRelatedProduct({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as ProductListElementDataType;

  const onChangeColor = (
    color: ColorResult & {
      source: 'rgb' | 'hex' | 'hsl';
    }
  ) => {
    if (color.source === 'rgb') {
      builder.setElementData(element.id, {
        overlay: `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})`,
      });
    } else {
      builder.setElementData(element.id, { overlay: color.hex });
    }
  };

  return (
    <div>
      <div className='mb-4 font-bold'>Title alignment</div>
      <InputTab
        defaultValue={data.contentAlign}
        options={[
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { contentAlign: v })}
      />
      <div className='mb-4 mt-4 font-bold'>Heading label</div>
      <TextEditorMinimal
        placeholder='Label'
        onChange={(value) =>
          builder.setElementData(element.id, {
            headingLabel: value,
          })
        }
        value={data.headingLabel}
        className='mb-4 w-full '
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='mb-4 mt-4 font-bold'>Heading size</div>
      <InputTab
        defaultValue={data.headingSize}
        options={[
          { label: 'Small', value: 'small' },
          { label: 'Medium', value: 'medium' },
          { label: 'Large', value: 'large' },
        ]}
        onChange={(v) =>
          builder.setElementData(element.id, {
            headingSize: v,
          })
        }
      />
      <div className='mb-4 mt-4 font-bold'>Description</div>
      <div className='relative w-full'>
        <TextEditorMinimal
          onChange={(e) => {
            builder.setElementData(element.id, {
              textLabel: e,
            });
          }}
          value={data.textLabel}
          output='html'
          editorProps={{
            attributes: {
              class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
            },
          }}
          editable
        />
      </div>

      <div className='mb-4 mt-4 font-bold'>Description style</div>
      <InputTab
        defaultValue={data.textStyle}
        options={[
          { label: 'Body', value: 'body' },
          { label: 'Subtitle', value: 'subtitle' },
          { label: 'UPPERCASE', value: 'uppercase' },
        ]}
        onChange={(v) =>
          builder.setElementData(element.id, {
            textStyle: v,
          })
        }
      />
      <div className='mb-2 mt-4 text-xs'>Limit</div>
      <InputSlider
        value={data.limit || 0}
        max={25}
        onChange={(val) =>
          builder.setElementData(element.id, {
            limit: val,
          })
        }
      />
      <div className='mb-4 mt-4 font-bold'>Product card</div>
      <h1 className='mb-2'>Image ratio</h1>
      <InputTab
        defaultValue={data.imageRatio}
        options={[
          { label: 'Adapt to image', value: 'adaptToImage' },
          { label: 'Portrait', value: 'portrait' },
          { label: 'Square', value: 'square' },
        ]}
        onChange={(v) =>
          builder.setElementData(element.id, {
            imageRatio: v,
          })
        }
      />
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showSecondImageOnHover'
          aria-label='Select'
          className='mx-2'
          checked={data.showSecondImageOnHover}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showSecondImageOnHover: !!checked,
            });
          }}
        />
        <label htmlFor='showSecondImageOnHover'>Show second image on hover</label>
      </div>
      <div className='mb-2 mt-4 text-xs'>Number of columns on desktop</div>
      <InputSlider
        value={data.totalCol || 4}
        min={1}
        max={6}
        onChange={(val) =>
          builder.setElementData(element.id, {
            totalCol: val,
          })
        }
      />
      <div className='mb-2 mt-4 text-xs'>Number of columns on mobile</div>
      <InputSlider
        value={data.totalColMobile || 2}
        min={1}
        max={3}
        onChange={(val) =>
          builder.setElementData(element.id, {
            totalColMobile: val,
          })
        }
      />
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='fullWidth'
          aria-label='Select'
          className='mx-2'
          checked={data.fullWidth}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              fullWidth: !!checked,
            });
          }}
        />
        <label htmlFor='fullWidth'>Make products full width</label>
      </div>
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='enableQuickAddButton'
          aria-label='Select'
          className='mx-2'
          checked={data.enableQuickAddButton}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              enableQuickAddButton: !!checked,
            });
          }}
        />
        <label htmlFor='enableQuickAddButton'>Enable quick add button</label>
      </div>
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showImage'
          aria-label='Select'
          className='mx-2'
          checked={data.showImage}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showImage: !!checked,
            });
          }}
        />
        <label htmlFor='showImage'>Show image</label>
      </div>
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showCategory'
          aria-label='Select'
          className='mx-2'
          checked={data.showCategory}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showCategory: !!checked,
            });
          }}
        />
        <label htmlFor='showCategory'>Show Category</label>
      </div>
      <PaddingSetting
        value={data.padding}
        onChange={(v) => builder.setElementData(element.id, { padding: v })}
      />
      <div className='mb-2 mt-4 font-bold'>Color scheme</div>
      <SchemeSetting
        value={data.colorScheme || DEFAULT_THEME_COLOR_SCHEME}
        onChange={(theme) => {
          builder.setElementData(element.id, {
            colorScheme: theme,
          });
        }}
      />
      <div className='mb-4 mt-1 text-xs'>
        To edit all your theme&apos;s colors, go to your color theme settings.
      </div>
      <BackgroundSetting
        value={data.background}
        onChange={(background) =>
          builder.setElementData(element.id, {
            background,
          })
        }
      />
      {data?.background?.type !== 'image' && (
        <>
          <div className='mb-4 mt-4 font-bold'>Overlay</div>
          <ColorPicker
            color={data.overlay}
            inputColor={data.overlay}
            onChangeColorComplete={(color) => onChangeColor(color)}
            onChangeInputColor={(e) =>
              builder.setElementData(element.id, { overlay: e.target.value })
            }
            onChangePercentage={(val) =>
              builder.setElementData(element.id, { overlayPercentage: val })
            }
          />
        </>
      )}
    </div>
  );
}
